/**
 * Explore — the unified detection workbench.
 *
 * One page, three columns: rail / list / detail.
 *  - Rail (~10%): switches between Rules / Scans / Leads / Datasets
 *  - List (~30%): items of the selected type, with type-specific filters
 *  - Detail (~60%): selected item, with type-specific panels + cross-links
 *
 * URL shape:
 *   /explore/:type                          → list of that type, no selection
 *   /explore/:type/:id                      → list + detail for the selected item
 *
 * Scans and Leads have no rail entry — they're operational output of running a
 * rule, not browsable libraries. /explore/scans/:id and /explore/leads/:id
 * still resolve as deep links from inside a rule.
 *
 * Cross-linking (rule → its scans, scan → its leads, etc.) updates the URL
 * via navigate() but never page-reloads — the shell stays mounted, list +
 * detail swap.
 */

import { useEffect, useState, ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import api from '../../api/axios';
import {
  RuleFolder as RulesIcon,
  Storage as DatasetsIcon,
  Gavel as StatutesIcon,
  Article as PressReleasesIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import exploreTheme from './exploreTheme';

import RuleList from './RuleList';
import RuleDetail from './RuleDetail';
import ScanDetail from './ScanDetail';
import LeadDetail from './LeadDetail';
import DatasetList from './DatasetList';
import DatasetDetail from './DatasetDetail';
import StatuteList from './StatuteList';
import StatuteDetail from './StatuteDetail';
import PressReleaseList from './PressReleaseList';
import PressReleaseDetail from './PressReleaseDetail';

// Scans and Leads are reachable via deep links from inside a rule (Results tab
// + lead detail), but they are NOT independent rail entries — the workbench
// is rule-first, and Scans/Leads are just the operational output of running
// a rule. Keep them in the union so the existing URLs resolve.
export type ExploreType =
  | 'rules' | 'scans' | 'leads' | 'datasets' | 'statutes' | 'press_releases';

// Only library entities appear in the rail.
const RAIL_ITEMS: { type: ExploreType; label: string; icon: ReactNode }[] = [
  { type: 'rules',          label: 'Rules',          icon: <RulesIcon /> },
  { type: 'datasets',       label: 'Datasets',       icon: <DatasetsIcon /> },
  { type: 'statutes',       label: 'Statutes',       icon: <StatutesIcon /> },
  { type: 'press_releases', label: 'Press Releases', icon: <PressReleasesIcon /> },
];

function isExploreType(s: string | undefined): s is ExploreType {
  return (
    s === 'rules' || s === 'scans' || s === 'leads' || s === 'datasets'
    || s === 'statutes' || s === 'press_releases'
  );
}

const Explore = () => {
  const { type: typeParam, id } = useParams<{
    type?: string; id?: string;
  }>();
  const navigate = useNavigate();
  const type: ExploreType = isExploreType(typeParam) ? typeParam : 'rules';

  // Bumped whenever the detail pane mutates a rule (status change, delete,
  // SQL edit) so the middle-column RuleList re-fetches without a page reload.
  const [ruleListNonce, setRuleListNonce] = useState(0);

  // Normalize URL: /explore → /explore/rules
  useEffect(() => {
    if (!typeParam) {
      navigate('/explore/rules', { replace: true });
    } else if (!isExploreType(typeParam)) {
      navigate('/explore/rules', { replace: true });
    }
  }, [typeParam, navigate]);

  // When landing on /explore/rules with no rule selected, jump to the most
  // recent active rule. Falls back to the most recently updated rule of any
  // status, and stays put if there are no rules at all.
  useEffect(() => {
    if (type !== 'rules' || id) return;
    let cancelled = false;
    api.get('/explore/rules/', { params: { status: 'active' } })
      .then((res) => {
        if (cancelled) return;
        const rules = res.data.rules || [];
        if (rules.length > 0) {
          navigate(`/explore/rules/${rules[0].rule_id}`, { replace: true });
          return;
        }
        return api.get('/explore/rules/').then((r) => {
          if (cancelled) return;
          const all = r.data.rules || [];
          if (all.length > 0) {
            navigate(`/explore/rules/${all[0].rule_id}`, { replace: true });
          }
        });
      })
      .catch(() => { /* leave the user on the empty Rules list */ });
    return () => { cancelled = true; };
  }, [type, id, navigate]);

  const renderList = () => {
    switch (type) {
      case 'rules':          return <RuleList    selectedId={id} refreshKey={ruleListNonce} onSelect={(rid) => navigate(`/explore/rules/${rid}`)} />;
      case 'datasets':       return <DatasetList selectedId={id} onSelect={(did) => navigate(`/explore/datasets/${did}`)} />;
      case 'statutes':       return <StatuteList selectedId={id} onSelect={(sid) => navigate(`/explore/statutes/${sid}`)} />;
      case 'press_releases': return <PressReleaseList selectedId={id} onSelect={(pid) => navigate(`/explore/press_releases/${pid}`)} />;
      // Deep-link cases (no rail entry, no middle column).
      case 'scans':
      case 'leads':
        return null;
    }
  };

  const renderDetail = () => {
    if (!id) return <EmptyDetail type={type} />;
    switch (type) {
      case 'rules':          return <RuleDetail    ruleId={id} onRuleMutated={() => setRuleListNonce((n) => n + 1)} />;
      case 'scans':          return <ScanDetail    scanId={id} />;
      case 'leads':          return <LeadDetail    leadId={id} />;
      case 'datasets':       return <DatasetDetail datasetId={id} />;
      case 'statutes':       return <StatuteDetail sourceId={id} />;
      case 'press_releases': return <PressReleaseDetail pressReleaseId={id} />;
    }
  };

  // Scans/Leads URLs are deep-links — they don't get a middle column.
  const showMiddleColumn = !(type === 'scans' || type === 'leads');

  return (
    // Scoped theme: the deep-slate workbench look applies only inside Explore.
    // The rest of the app keeps the default theme (src/theme.ts).
    <ThemeProvider theme={exploreTheme}>
    <Layout>
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 64px)', // AppBar height
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Left rail */}
        <Paper
          square
          elevation={0}
          sx={{
            width: 220,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            py: 2,
            backgroundColor: 'background.paper',
          }}
        >
          {RAIL_ITEMS.map((item) => {
            const active = item.type === type;
            return (
              <Box
                key={item.type}
                onClick={() => navigate(`/explore/${item.type}`)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2.5,
                  py: 1.25,
                  mx: 1,
                  mb: 0.5,
                  cursor: 'pointer',
                  borderRadius: 1,
                  color: active ? 'primary.main' : 'text.secondary',
                  backgroundColor: active ? 'rgba(58, 68, 82, 0.08)' : 'transparent',
                  fontWeight: active ? 600 : 400,
                  '&:hover': {
                    backgroundColor: 'rgba(58, 68, 82, 0.05)',
                    color: 'primary.main',
                  },
                }}
              >
                {item.icon}
                <Typography sx={{ fontWeight: 'inherit' }}>{item.label}</Typography>
              </Box>
            );
          })}
        </Paper>

        {/* Middle list — hidden for deep-link routes (scan / lead detail) */}
        {showMiddleColumn && (
          <Box
            sx={{
              width: 380,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              overflowY: 'auto',
              backgroundColor: 'background.default',
            }}
          >
            {renderList()}
          </Box>
        )}

        {/* Right detail */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            backgroundColor: 'background.default',
          }}
        >
          {renderDetail()}
        </Box>
      </Box>
    </Layout>
    </ThemeProvider>
  );
};

const EmptyDetail = ({ type }: { type: ExploreType }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text.secondary',
      flexDirection: 'column',
      gap: 1,
      p: 4,
    }}
  >
    <Typography variant="h6">No {type.slice(0, -1)} selected</Typography>
    <Typography variant="body2">Pick one from the list to see details.</Typography>
  </Box>
);

export default Explore;
